<template>
  <el-dialog
    class="dialog-inner"
    :model-value="visible"
    :before-close="() => {
      emit('close');
    }"
    width="500"
  >
    <template #header>
      <div class="my-header">
        <h4 class="px-2 text-xl text-[#344767] font-semibold">
          提现
        </h4>
      </div>
    </template>

    <div class="px-2 pb-3 relative">
      <div class="with-cash-title">
        <label>可用余额<strong>{{ data.availableBalance || 0 }}</strong>元，可提现<strong>{{ data.withdrawableAmount }}</strong>元</label>
      </div>

      <el-form
        ref="formRef"
        require-asterisk-position="right"
        label-position="top"
        :model="formValues"
        :rules="formRules"
      >
        <el-form-item prop="amount" label="提现金额（元）">
          <el-input v-model="formValues.amount" size="large" autocomplete="new-password" placeholder="请输入" @keyup.enter="onConfirm" />
        </el-form-item>
        <el-form-item prop="password" label="支付密码">
          <secure-password
            v-if="visible"
            ref="securePasswordRef"
            name="password"
            class="w-full"
            placeholder="请输入支付密码"
            :random-keys="randomKeys"
            @change="onPassChange"
          />
        </el-form-item>
        <el-form-item prop="remark" label="备注">
          <el-input
            v-model="formValues.remark"
            type="textarea"
            clearable
            resize="none"
            autocomplete="nony"
            show-word-limit
            :rows="4"
            :maxlength="500"
            placeholder="请输入"
            @keydown="(e: KeyboardEvent | Event) => {
              if ((e as KeyboardEvent).keyCode === 13) {
                e.preventDefault();
                onConfirm();
              }
            }"
          />
        </el-form-item>
      </el-form>
    </div>

    <template #footer>
      <div class="flex justify-center">
        <el-button @click="emit('close')">
          取消
        </el-button>
        <el-button type="primary" :loading="loading" @click="onConfirm">
          确定
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { checkNumber, isNull } from '@core/tools';
import { ElMessage, FormInstance, FormRules } from 'element-plus';
import { IRandomKeys, ISecurePassword, queryRandomPassKeys, SecurePassword } from '@vue3/components';
import { nextTick, PropType, ref, toRaw, watch } from 'vue';
import { IFinanceWallet, IFinanceLianWithdraw } from '@core/main/types';

const emit = defineEmits(['close', 'confirm']);

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  data: {
    type: Object as PropType<IFinanceWallet>,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  term: {
    type: String as PropType<'server' | 'company' | 'agent' | 'saas'>,
    default: 'server',
  },
  payPhone: {
    type: String,
    default: '',
  },
});

const formRef = ref<FormInstance>();
const formValues = ref({} as IFinanceLianWithdraw);
const passObj = ref({} as ISecurePassword);
const randomKeys = ref({} as IRandomKeys);

const securePasswordRef = ref<InstanceType<typeof SecurePassword>>();

const formRules = ref<FormRules<any>>({
  amount: [
    {
      required: true,
      message: '请输入提现金额',
    },
    {
      validator: (rule: any, value: string, callback: any) => {
        if (checkNumber(String(formValues.value.amount), '提现金额', 2)) {
          callback(checkNumber(String(formValues.value.amount), '提现金额', 2));
        }
        callback();
      },
    },
    {
      validator: (rule: any, value: string, callback: any) => {
        if (!formValues.value.amount || Number(formValues.value.amount) <= 0) {
          callback(new Error('提现金额不能为0'));
        }
        callback();
      },
    },
    {
      validator: (rule: any, value: string, callback: any) => {
        if (!isNull(formValues.value.amount) && Number(formValues.value.amount) > Number(props.data.withdrawableAmount)) {
          callback(new Error('提现金额必须小于可提现金额'));
        }
        callback();
      },
    },
  ],
  password: [
    {
      required: true,
      message: '请输入提现密码',
    },
    {
      required: true,
      validator: (rule: any, value: string, callback: any) => {
        if (passObj.value.hash && passObj.value.length < 6) {
          callback(new Error('支付密码必须是6-20位'));
        }
        callback();
      },
    },
  ],
});

async function fetchRandomKeys() {
  try {
    randomKeys.value = await queryRandomPassKeys({
      propertyValue: Number(props.data.accountId),
      prefix: 'pay/unified-lian-account/get-random-key',
      apiMethod: 'post',
    });
  } catch (error) {
    console.log(error);
  }
}

function onPassChange({ password, hash, length }: ISecurePassword) {
  formValues.value.password = password;
  passObj.value = {
    password,
    hash,
    length: length || 0,
  };
  formRef.value?.validateField('password');
  if (length >= 6 && length <= 20) formRef.value?.validateField('rePassword');
}

function onConfirm() {
  const amount = formValues.value.amount;

  if (amount && !isNaN(amount) && amount >= 50000 && !formValues.value.remark) {
    return ElMessage.error('金额大于等于5万, 请填写备注');
  }

  formRef.value?.validate(async (valid) => {
    if (valid) {
      const params = {
        lianId: Number(props.data.accountId),
        ...toRaw(formValues.value),
        randomKey: randomKeys.value.randomKey,
      } as IFinanceLianWithdraw;

      emit('confirm', params);
    }
  });
}

function resetForm() {
  for (const key in formValues.value) {
    formValues.value[key] = undefined;
  }
}

watch(
  () => props.visible,
  (val) => {
    if (val) {
      fetchRandomKeys();
      resetForm();
      nextTick(() => {
        formRef.value?.resetFields();
      });
    } else {
      securePasswordRef.value?.clearPassword();
    }
  },
);
</script>

<style lang="scss" scoped>
:deep(.dialog-inner .el-dialog__body) {
  overflow-y: unset !important;
}

.with-cash-title {
  padding: 0 0 10px 0;

  span {
    font-weight: 600;
  }

  label {
    font-weight: normal;

    strong {
      color: #f00;
    }
  }
}

.with-cash-content {
  padding: 26px 0;
  box-sizing: border-box;

  .lianlian-pass-field {
    width: 300px;
  }
}
</style>
